import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import {
  AppUserAttachment,
  AppUserProfile,
  AppUserService,
  AttachmentService,
  PaymentService,
  TokenStorageService
} from '../../core/services';
import {
  appendUniqueById,
  createListPaginationState,
  hasMoreByTotal,
  resolveRequestPage,
  resetListPaginationState,
  updateAfterFetch
} from '../../core/utils/list-pagination.util';
import {
  EntityListComponent,
  EntityListMenuAction,
  TableHeaderAndValueKeySets
} from '../../core/ui/entity-list/entity-list.component';
import { TranslatePipe } from '../../core/pipes/translate.pipe';
import { MakePaymentComponent, PaymentSaveEvent } from './make-payment/make-payment.component';
import { APP_DEVELOPED_BY, PaymentMethodEnum } from '../../core/constants/app-constants';
import {
  buildPaymentReceiptPdfAttachment,
  downloadPaymentReceiptPdf,
  downloadPaymentSlipPdf,
  PaymentReceiptData,
  printPaymentReceiptPdf,
  printPaymentSlipPdf
} from './payment-receipt.util';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';

interface AuditActor {
  id?: number;
  organizationName?: string;
  name?: string;
  username?: string;
  email?: string;
}

interface PaymentItem {
  id: number;
  txnNo?: number | string;
  flatRenterMapping?: {
    id?: number;
    flat?: { flatName?: string; floorName?: string; negativeChargeName?: string; house?: { houseName?: string } };
    renter?: { renterName?: string; renterMobile?: string; renterEmail?: string };
  };
  paymentDate?: string;
  paymentMonth?: number;
  paymentYear?: string | number;
  rentAmount?: number;
  gasBillAmount?: number;
  electricBillAmount?: number;
  waterBillAmount?: number;
  serviceChargeAndOtherAmount?: number;
  negativeChargeAmount?: number;
  advanceAdjustedAmount?: number;
  paymentMethod?: number | string;
  remarks?: string;
  status: number;
  createdOn?: string;
  updatedOn?: string;
  createdBy?: number;
  updatedBy?: number;
  createdByUser?: AuditActor;
  updatedByUser?: AuditActor;
  paymentHistoryList?: PaymentHistoryItem[];
}

interface PaymentHistoryItem {
  id?: number;
  txnNo?: number | string;
  paymentDate?: string;
  paymentMonth?: number;
  paymentYear?: string | number;
  paymentMethod?: number | string;
  rentAmount?: number;
  gasBillAmount?: number;
  electricBillAmount?: number;
  waterBillAmount?: number;
  serviceChargeAndOtherAmount?: number;
  negativeChargeAmount?: number;
  advanceAdjustedAmount?: number;
  createdOn?: string;
  remarks?: string;
  createdBy?: number;
  updatedBy?: number;
  createdByUser?: AuditActor;
  updatedByUser?: AuditActor;
}

interface PaymentRow extends PaymentItem {
  mappingTopLine: string;
  mappingBottomLine: string;
  txnNoLabel: string;
  payMonthLabel: string;
  paymentDateLabel: string;
  totalAmountLabel: string;
  statusLabelText: string;
}

interface PaymentCreatePrefillContext {
  pendingMappingId: number | null;
  houseId: number | null;
  flatId: number | null;
  renterId: number | null;
  paymentMonth: number | null;
  paymentYear: number | null;
}

@Component({
  selector: 'app-payment-list',
  standalone: true,
  imports: [CommonModule, FormsModule, EntityListComponent, MakePaymentComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.css']
})
export class PaymentListComponent implements OnInit {
  isAuthenticated = false;
  isLoading = false;
  isLoadingMore = false;
  error: string | null = null;
  status: string | null = null;

  paymentList: PaymentRow[] = [];
  filters: Record<string, any> = {};
  yearlySummaryText = '';
  private readonly paginationState = createListPaginationState();

  selectedPayment: PaymentItem | null = null;
  pendingDeletePayment: PaymentItem | null = null;
  selectedPaymentHistory: PaymentHistoryItem[] = [];
  selectedHistoryItem: PaymentHistoryItem | null = null;
  isDetailsOpen = false;
  isHistoryOpen = false;
  isHistoryDetailsOpen = false;
  isEditOpen = false;
  createPrefillContext: PaymentCreatePrefillContext | null = null;
  isDeleting = false;
  isSendReceiptOpen = false;
  isSendingReceiptEmail = false;
  isPreparingWhatsapp = false;
  sendReceiptEmail = '';
  sendReceiptWhatsappNumber = '';
  sendReceiptError: string | null = null;
  sendReceiptSuccess: string | null = null;
  receiptOrganizationName = '';
  receiptBrandName = 'HTALLY';
  receiptBrandLogoUrl: string | null = null;
  private readonly activePaymentMenuActions: EntityListMenuAction[] = [
    { key: 'receiptDownload', label: 'action.receiptDownload', requiredService: 'RECEIPT_DOWNLOAD' },
    { key: 'receiptPrint', label: 'action.receiptPrint', requiredService: 'RECEIPT_PRINT' },
    { key: 'sendReceipt', label: 'action.sendReceipt', requiredService: 'RECEIPT_SEND_EMAIL' },
    { key: 'edit', label: 'action.edit', requiredService: 'PAYMENT_CREATE' },
    { key: 'delete', label: 'action.delete', danger: true, requiredService: 'PAYMENT_DELETE' },
    { key: 'history', label: 'action.history', visibleWhen: 'updatedOn', requiredService: 'PAYMENT_CREATE' }
  ];
  private readonly deletedPaymentMenuActions: EntityListMenuAction[] = [
    { key: 'history', label: 'action.history' }
  ];

  tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    colorKey: 'status',
    'table.transactionNo': 'txnNoLabel',
    'table.flat': {
      topLineKey: 'mappingTopLine',
      bottomLineKyes: ['mappingBottomLine']
    },
    'table.paymentMonth': 'payMonthLabel',
    'table.paymentDate': 'paymentDateLabel',
    'table.amount': 'totalAmountLabel'
  };
  readonly paymentListColumnTemplate = 'minmax(84px, 0.7fr) minmax(190px, 1.9fr) minmax(132px, 1.05fr) minmax(118px, 0.95fr) minmax(92px, 0.72fr)';

  get deletedMode(): boolean {
    return Number(this.filters?.['status']) === 0;
  }

  get paymentMenuActions(): EntityListMenuAction[] {
    return this.deletedMode ? this.deletedPaymentMenuActions : this.activePaymentMenuActions;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly payments: PaymentService,
    private readonly tokens: TokenStorageService,
    private readonly appUsers: AppUserService,
    private readonly attachments: AttachmentService
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.tokens.hasActiveSession;
    if (!this.isAuthenticated) {
      this.error = 'Please log in first.';
      return;
    }

    this.status = 'Loading payment list...';
    this.fetchPayments(true);
    this.fetchYearlySummary();
    this.loadReceiptBranding();
    this.tryOpenCreateFromRouteParams();
  }

  get hasMorePayments(): boolean {
    return hasMoreByTotal(this.paymentList, this.paginationState);
  }

  fetchPayments(reset = true): void {
    if ((reset && this.isLoading) || (!reset && (this.isLoading || this.isLoadingMore || !this.hasMorePayments))) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.error = null;
      this.paymentList = [];
      resetListPaginationState(this.paginationState);
    } else {
      this.isLoadingMore = true;
    }

    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }
    const requestPage = resolveRequestPage(this.paginationState, reset);
    this.payments.list({
      pagination: this.paginationState.pagination,
      pageNo: requestPage,
      rowCount: this.paginationState.rowCount,
      withTotals: true,
      filters
    })
      .pipe(finalize(() => {
        if (reset) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
      }))
      .subscribe({
        next: (res: any) => {
          if (res?.code !== '000') {
            this.error = res?.message || 'Unknown error occurred.';
            return;
          }

          const data = Array.isArray(res?.data) ? res.data : [];
          const nextRows: PaymentRow[] = data.map((item: PaymentItem) => {
            const total = this.totalAmount(item);
            return {
              ...item,
              mappingTopLine: item.flatRenterMapping?.flat?.flatName || 'N/A',
              mappingBottomLine: this.mappingBottomLine(item),
              txnNoLabel: item.txnNo ? `${item.txnNo}` : 'N/A',
              payMonthLabel: this.paymentMonthLabel(item.paymentMonth, item.paymentYear),
              paymentDateLabel: this.formatDate(item.paymentDate),
              totalAmountLabel: this.formatAmount(total),
              statusLabelText: item.status === 1 ? 'Active' : 'Inactive'
            };
          });
          updateAfterFetch(this.paginationState, res?.totalRows, requestPage, nextRows.length);
          this.paymentList = reset ? nextRows : appendUniqueById(this.paymentList, nextRows);

          this.status = `Total payments: ${this.paginationState.totalRows || this.paymentList.length}`;
        },
        error: (err: any) => {
          this.error = err?.message || 'Error fetching payments';
        }
      });
  }

  fetchYearlySummary(): void {
    const currentYear = new Date().getFullYear();
    this.payments.yearlySummary({
      filters: { paymentYear: currentYear }
    }).subscribe({
      next: (res: any) => {
        const summary = res?.data;
        if (!summary) {
          this.yearlySummaryText = '';
          return;
        }

        const rawTotal = Number(summary?.totalAmount ?? summary?.totalPaid ?? summary?.yearlyTotal ?? 0);
        if (!Number.isFinite(rawTotal) || rawTotal <= 0) {
          this.yearlySummaryText = '';
          return;
        }

        this.yearlySummaryText = `Year ${currentYear} total: ${this.formatAmount(rawTotal)}`;
      },
      error: () => {
        this.yearlySummaryText = '';
      }
    });
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...filters };
    this.fetchPayments(true);
  }

  onLoadMoreFromList(): void {
    this.fetchPayments(false);
  }

  openCreateModal(): void {
    this.selectedPayment = null;
    this.createPrefillContext = null;
    this.isEditOpen = true;
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedPayment = item as PaymentItem;
    this.isDetailsOpen = true;
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.selectedPayment = item as PaymentItem;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeletePayment = item as PaymentItem;
  }

  closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeletePayment = null;
  }

  confirmDeletePayment(): void {
    const payment = this.pendingDeletePayment;
    if (!payment || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.error = null;

    this.payments.delete({ id: payment.id })
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.pendingDeletePayment = null;
          this.fetchPayments(true);
        },
        error: (err: any) => {
          this.pendingDeletePayment = null;
          this.error = err?.message || 'Failed to delete payment.';
        }
      });
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
  }

  closeHistoryModal(): void {
    this.isHistoryOpen = false;
    this.selectedPaymentHistory = [];
  }

  openHistoryDetails(item: PaymentHistoryItem): void {
    this.selectedHistoryItem = item;
    this.isHistoryDetailsOpen = true;
  }

  closeHistoryDetailsModal(): void {
    this.isHistoryDetailsOpen = false;
    this.selectedHistoryItem = null;
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.selectedPayment = null;
    this.createPrefillContext = null;
  }

  openSendReceiptModal(payment: PaymentItem): void {
    this.selectedPayment = payment;
    this.sendReceiptEmail = `${payment.flatRenterMapping?.renter?.renterEmail ?? ''}`.trim();
    this.sendReceiptWhatsappNumber = `${payment.flatRenterMapping?.renter?.renterMobile ?? ''}`.trim();
    this.sendReceiptError = null;
    this.sendReceiptSuccess = null;
    this.isSendReceiptOpen = true;
  }

  closeSendReceiptModal(): void {
    this.isSendReceiptOpen = false;
    this.sendReceiptEmail = '';
    this.sendReceiptWhatsappNumber = '';
    this.sendReceiptError = null;
    this.sendReceiptSuccess = null;
    this.isSendingReceiptEmail = false;
    this.isPreparingWhatsapp = false;
  }

  onEditSave(event: PaymentSaveEvent): void {
    if (event?.mode === 'edit') {
      this.isEditOpen = false;
      this.selectedPayment = null;
    }
    this.fetchPayments(true);
    this.fetchYearlySummary();
  }

  sendReceiptLabel(): string {
    const txn = this.selectedPayment?.txnNo ? `${this.selectedPayment.txnNo}` : `${this.selectedPayment?.id ?? 'N/A'}`;
    const month = this.paymentMonthLabel(this.selectedPayment?.paymentMonth, this.selectedPayment?.paymentYear);
    return `${txn} | ${month}`;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return `${value}`;
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;

    return `${day}-${month}-${year} ${`${hours12}`.padStart(2, '0')}:${minutes} ${period}`;
  }

  private mappingBottomLine(item: PaymentItem): string {
    const renterName = item.flatRenterMapping?.renter?.renterName;
    const houseName = item.flatRenterMapping?.flat?.house?.houseName;
    return [renterName, houseName].filter((v) => !!v).join(' | ') || 'N/A';
  }

  public paymentMonthLabel(month: number | undefined, year: number | string | undefined): string {
    const monthNo = Number(month);
    if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
      return 'N/A';
    }
    const monthName = new Date(2000, monthNo - 1, 1).toLocaleString('en-US', { month: 'long' });
    return `${monthName} ${year ?? ''}`.trim();
  }

  onMenuAction(event: { actionKey: string; item: Record<string, any> }): void {
    const payment = event.item as PaymentItem;
    if (event.actionKey === 'history') {
      const history = Array.isArray(payment.paymentHistoryList) ? payment.paymentHistoryList : [];
      this.selectedPayment = payment;
      this.selectedPaymentHistory = history;
      this.isHistoryOpen = true;
    } else if (event.actionKey === 'receiptDownload') {
      void downloadPaymentReceiptPdf(this.buildReceiptData(payment));
    } else if (event.actionKey === 'receiptPrint') {
      void printPaymentReceiptPdf(this.buildReceiptData(payment));
    } else if (event.actionKey === 'paymentSlipDownload') {
      void downloadPaymentSlipPdf(this.buildReceiptData(payment));
    } else if (event.actionKey === 'paymentSlipPrint') {
      void printPaymentSlipPdf(this.buildReceiptData(payment));
    } else if (event.actionKey === 'sendReceipt') {
      this.openSendReceiptModal(payment);
    }
  }

  async sendReceiptByEmail(): Promise<void> {
    const paymentId = Number(this.selectedPayment?.id);
    const email = `${this.sendReceiptEmail ?? ''}`.trim();
    if (!Number.isFinite(paymentId)) {
      this.sendReceiptError = 'Invalid payment selected.';
      return;
    }
    if (!email) {
      this.sendReceiptError = 'Email is required.';
      return;
    }

    this.isSendingReceiptEmail = true;
    this.sendReceiptError = null;
    this.sendReceiptSuccess = null;

    let pdfAttachment: { fileName: string; pdfBase64: string };
    try {
      pdfAttachment = await buildPaymentReceiptPdfAttachment(this.buildReceiptData(this.selectedPayment as PaymentItem));
    } catch {
      this.isSendingReceiptEmail = false;
      this.sendReceiptError = 'Failed to prepare receipt PDF.';
      return;
    }

    this.payments.sendReceiptByEmail({ paymentId, email, ...pdfAttachment })
      .pipe(finalize(() => (this.isSendingReceiptEmail = false)))
      .subscribe({
        next: (res: any) => {
          this.sendReceiptSuccess = `${res?.message ?? 'Receipt sent by email successfully.'}`;
        },
        error: (err: any) => {
          this.sendReceiptError = err?.message || 'Failed to send receipt by email.';
        }
      });
  }

  sendReceiptByWhatsapp(): void {
    const paymentId = Number(this.selectedPayment?.id);
    const whatsappNumber = `${this.sendReceiptWhatsappNumber ?? ''}`.trim();
    if (!Number.isFinite(paymentId)) {
      this.sendReceiptError = 'Invalid payment selected.';
      return;
    }
    if (!whatsappNumber) {
      this.sendReceiptError = 'WhatsApp number is required.';
      return;
    }

    this.isPreparingWhatsapp = true;
    this.sendReceiptError = null;
    this.sendReceiptSuccess = null;

    this.payments.prepareReceiptWhatsapp({ paymentId, whatsappNumber })
      .pipe(finalize(() => (this.isPreparingWhatsapp = false)))
      .subscribe({
        next: (res: any) => {
          const whatsappUrl = `${res?.whatsappUrl ?? ''}`.trim();
          if (!whatsappUrl) {
            this.sendReceiptError = 'Failed to prepare WhatsApp message.';
            return;
          }
          window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
          this.sendReceiptSuccess = `${res?.message ?? 'WhatsApp message prepared successfully.'}`;
        },
        error: (err: any) => {
          this.sendReceiptError = err?.message || 'Failed to prepare WhatsApp message.';
        }
      });
  }

  historyAmount(item: PaymentHistoryItem): number {
    return Number(item.rentAmount ?? 0)
      + Number(item.gasBillAmount ?? 0)
      + Number(item.electricBillAmount ?? 0)
      + Number(item.waterBillAmount ?? 0)
      + Number(item.serviceChargeAndOtherAmount ?? 0)
      - Number(item.negativeChargeAmount ?? 0)
      - Number(item.advanceAdjustedAmount ?? 0);
  }

  historyByLabel(item: PaymentHistoryItem): string {
    if (item.updatedBy != null || item.updatedByUser) {
      return this.actorLabel(item.updatedByUser, item.updatedBy);
    }
    return this.actorLabel(item.createdByUser, item.createdBy);
  }

  hasRecordedUpdate(item: { createdOn?: string | null; updatedOn?: string | null }): boolean {
    const updatedOn = `${item.updatedOn ?? ''}`.trim();
    if (!updatedOn) {
      return false;
    }
    const createdOn = `${item.createdOn ?? ''}`.trim();
    return !createdOn || updatedOn !== createdOn;
  }

  hasUpdatedActor(item: { updatedBy?: number | null; updatedByUser?: AuditActor | null }): boolean {
    return item.updatedBy != null || !!item.updatedByUser;
  }

  private totalAmount(item: PaymentItem): number {
    return Number(item.rentAmount ?? 0)
      + Number(item.gasBillAmount ?? 0)
      + Number(item.electricBillAmount ?? 0)
      + Number(item.waterBillAmount ?? 0)
      + Number(item.serviceChargeAndOtherAmount ?? 0)
      - Number(item.negativeChargeAmount ?? 0)
      - Number(item.advanceAdjustedAmount ?? 0);
  }

  public formatAmount(value: number): string {
    return `${Number(value ?? 0).toLocaleString('en-US')}`;
  }

  public paymentMethodLabel(value: number | string | null | undefined): string {
    const method = Number(value);
    switch (method) {
      case PaymentMethodEnum.CASH:
        return 'Cash';
      case PaymentMethodEnum.BANK:
        return 'Bank';
      case PaymentMethodEnum.CHEQUE:
        return 'Cheque';
      case PaymentMethodEnum.MOBILE_BANKING:
        return 'Mobile Banking';
      case PaymentMethodEnum.ADJUST_FROM_ADVANCE:
        return 'Adjust From Advance';
      case PaymentMethodEnum.OTHER:
        return 'Other';
      default:
        return 'N/A';
    }
  }

  public hasPositiveAmount(value: number | string | null | undefined): boolean {
    return Number(value ?? 0) > 0;
  }

  actorLabel(user: AuditActor | null | undefined, fallbackId?: number | null): string {
    const label = `${user?.organizationName ?? user?.name ?? user?.username ?? user?.email ?? ''}`.trim();
    if (label) {
      return label;
    }
    return fallbackId != null ? `User #${fallbackId}` : 'N/A';
  }

  private buildReceiptData(payment: PaymentItem): PaymentReceiptData {
    const txn = payment.txnNo ? `${payment.txnNo}` : `${payment.id ?? 'N/A'}`;
    const monthLabel = this.paymentMonthLabel(payment.paymentMonth, payment.paymentYear);
    const paymentDate = this.formatDate(payment.paymentDate);
    const createdOn = this.formatDateTime(payment.createdOn);
    const flatName = payment.flatRenterMapping?.flat?.flatName || 'N/A';
    const floorName = payment.flatRenterMapping?.flat?.floorName || 'N/A';
    const houseName = payment.flatRenterMapping?.flat?.house?.houseName || 'N/A';
    const renterName = payment.flatRenterMapping?.renter?.renterName || 'N/A';
    const paymentMethod = this.paymentMethodLabel(payment.paymentMethod);

    const amountRows = ([
      ['Rent', Number(payment.rentAmount ?? 0)] as [string, number],
      ['Gas', Number(payment.gasBillAmount ?? 0)] as [string, number],
      ['Electric', Number(payment.electricBillAmount ?? 0)] as [string, number],
      ['Water', Number(payment.waterBillAmount ?? 0)] as [string, number],
      ['Service Charge + Other', Number(payment.serviceChargeAndOtherAmount ?? 0)] as [string, number],
      [payment.flatRenterMapping?.flat?.negativeChargeName || 'Negative Charge', Number(payment.negativeChargeAmount ?? 0) * -1] as [string, number],
      ['Adjust From Advance', Number(payment.advanceAdjustedAmount ?? 0) * -1] as [string, number]
    ]).filter(([, amount]) => amount !== 0);

    const total = this.totalAmount(payment);
    const generatedBy = this.resolveCurrentUserLabel();
    return {
      txn,
      monthLabel,
      paymentDate,
      createdOn,
      paymentMethod,
      status: 'PAID',
      organizationName: this.receiptOrganizationName,
      flatName,
      floorName,
      houseName,
      renterName,
      amountRows,
      total,
      brandName: this.receiptBrandName,
      brandLogoUrl: this.receiptBrandLogoUrl,
      generatedOn: this.formatDateTime(new Date().toISOString()),
      generatedBy,
      developedBy: APP_DEVELOPED_BY
    };
  }

  private loadReceiptBranding(): void {
    this.appUsers.me().subscribe({
      next: (profile) => {
        this.resolveReceiptBrandingProfile(profile);
      },
      error: () => {
        this.loadReceiptBrandingFromListFallback();
      }
    });
  }

  private loadReceiptBrandingFromListFallback(): void {
    this.appUsers.basicInfoFromList().subscribe({
      next: (profile) => this.resolveReceiptBrandingProfile(profile),
      error: () => {
        this.receiptOrganizationName = '';
        this.receiptBrandName = 'HTALLY';
        this.receiptBrandLogoUrl = null;
      }
    });
  }

  private resolveReceiptBrandingProfile(profile: AppUserProfile | null): void {
    this.appUsers.resolveAccountOwnerProfile(profile).subscribe({
      next: (ownerProfile) => this.applyReceiptBranding(ownerProfile ?? profile),
      error: () => this.applyReceiptBranding(profile)
    });
  }

  private applyReceiptBranding(profile: AppUserProfile | null): void {
    const organizationName = `${profile?.organizationName ?? ''}`.trim();
    const userName = `${profile?.name ?? ''}`.trim();
    this.receiptOrganizationName = organizationName;
    this.receiptBrandName = organizationName || userName || 'HTALLY';

    const attachmentId = this.firstAttachmentId(profile?.attachments);
    if (!Number.isFinite(attachmentId)) {
      this.receiptBrandLogoUrl = null;
      return;
    }

    this.attachments.previewUrlByAttachmentIdCached(attachmentId).subscribe({
      next: (url: string) => {
        this.receiptBrandLogoUrl = `${url ?? ''}`.trim() || null;
      },
      error: () => {
        this.receiptBrandLogoUrl = null;
      }
    });
  }

  private firstAttachmentId(attachments: AppUserAttachment[] | undefined): number {
    const attachmentId = Number(attachments?.[0]?.id);
    return Number.isFinite(attachmentId) ? attachmentId : Number.NaN;
  }

  private resolveCurrentUserLabel(): string {
    const token = `${this.tokens.accessToken ?? ''}`.trim();
    if (!token) {
      return 'N/A';
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return 'N/A';
    }

    try {
      const base64 = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
      const jsonText = atob(base64);
      const payload = JSON.parse(jsonText) as Record<string, unknown>;
      const label = `${payload?.['name'] ?? payload?.['username'] ?? payload?.['preferred_username'] ?? payload?.['sub'] ?? ''}`.trim();
      return label || 'N/A';
    } catch {
      return 'N/A';
    }
  }

  private tryOpenCreateFromRouteParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const hasPrefillParams = params.has('pendingMappingId') || params.has('flatId') || params.has('houseId');
    if (!hasPrefillParams) {
      return;
    }

    const asNumber = (key: string): number | null => {
      const raw = params.get(key);
      if (raw === null || raw === undefined || `${raw}`.trim() === '') {
        return null;
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    };

    this.createPrefillContext = {
      pendingMappingId: asNumber('pendingMappingId'),
      houseId: asNumber('houseId'),
      flatId: asNumber('flatId'),
      renterId: asNumber('renterId'),
      paymentMonth: asNumber('paymentMonth'),
      paymentYear: asNumber('paymentYear')
    };

    this.selectedPayment = null;
    this.isEditOpen = true;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });
  }
}

